/**
 * At-cost promotion pricing: a line, or some of its units, charged at the
 * clinic's cost. Shared by the promotion applier, quantity reservation and
 * discount normalization so every path keeps the line at cost.
 */
import { DiscountUse } from "@/types/billing/discount";

/**
 * What an at-cost discount takes off a line so `units` of it cost `unitCost`
 * each. `earlier` holds the discounts already on the line. As the price
 * buckets do, each one is spread over the units with the most left to pay,
 * and the at-cost units are the ones left with the most after that, so
 * stacking never goes below cost and a unit cap leaves the other units'
 * discounts alone. Mirrors `at_cost_discount` in services/promotion.py.
 */
export function atCostDiscountAmount(
  price: number,
  quantity: number,
  units: number,
  unitCost: number,
  earlier: Pick<DiscountUse, "amount" | "quantity">[] = [],
): number {
  if (quantity <= 0 || units <= 0) return 0;
  const covered = Math.min(units, quantity);
  const whole = Number.isInteger(quantity);
  // Units grouped by what they have left to pay: one bucket per distinct
  // remainder rather than one per unit, so a huge line stays cheap. A
  // fractional line's buckets hold fractions of a unit.
  let buckets: { left: number; count: number }[] = [
    { left: price, count: quantity },
  ];
  for (const use of earlier) {
    const reach = Math.min(use.quantity || quantity, quantity);
    let span = whole ? Math.floor(reach) : reach;
    if (span <= 0) continue;
    const perUnit = use.amount / span;
    buckets.sort((a, b) => b.left - a.left);
    const touched: typeof buckets = [];
    const untouched: typeof buckets = [];
    for (const bucket of buckets) {
      const take = Math.min(bucket.count, span);
      if (take > 0) {
        touched.push({
          left: Math.max(0, bucket.left - perUnit),
          count: take,
        });
      }
      if (bucket.count > take) {
        untouched.push({ left: bucket.left, count: bucket.count - take });
      }
      span -= take;
    }
    buckets = [...touched, ...untouched];
  }
  buckets.sort((a, b) => b.left - a.left);
  let toPrice = covered;
  let total = 0;
  for (const bucket of buckets) {
    if (toPrice <= 0) break;
    const take = Math.min(bucket.count, toPrice);
    total += Math.max(0, bucket.left - unitCost) * take;
    toPrice -= take;
  }
  return Math.max(0, Math.round(total));
}

/** The unit cost an at-cost promotion use was priced from, unsaved or saved. */
export function atCostUnitCostOf(use: DiscountUse): number | null {
  return (
    use.createDiscount?.atCostUnitCost ?? use.discount?.atCostUnitCost ?? null
  );
}

/**
 * Recompute a line's at-cost promotion uses at its current price and
 * quantity, so an edit keeps the line at cost instead of keeping the old
 * discount. Pass uses already normalized at the new price, so the earlier
 * discounts counted toward the floor are current.
 */
export function repriceAtCostUses(
  uses: DiscountUse[],
  price: number,
  quantity: number,
): DiscountUse[] {
  const earlier: DiscountUse[] = [];
  return uses.map((use) => {
    const unitCost = atCostUnitCostOf(use);
    // An unsaved use covers what it was applied over, not the units the
    // sequential recalc found left to discount, so coverage an earlier
    // discount took comes back when that discount is removed. A saved use's
    // coverage is rounded up to whole units; a fractional line covers no
    // more than it holds.
    const covered = Math.min(
      use.createDiscount
        ? (use.createDiscount.quantity ?? quantity)
        : use.quantity,
      quantity,
    );
    const next =
      unitCost == null
        ? use
        : {
            ...use,
            // The API takes whole units; the amount uses the fraction.
            quantity: Math.ceil(covered),
            amount: atCostDiscountAmount(
              price,
              quantity,
              covered,
              unitCost,
              earlier,
            ),
          };
    earlier.push(next);
    return next;
  });
}
